import React, { useState, useEffect, FC, useRef } from 'react';
import { Box, Container, Grid, Button } from '@mui/material';
import axios, { API } from 'lib/config/axios';
import { useDispatch } from 'react-redux';
import { setLoading } from 'redux/features/ui-slice';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import PATHS from 'lib/navigation/paths';
import StyledBox from 'views/rebranded-module/events-module/MotorcycleEventsDetailsView/MotorcycleEventsDetailsView.styles';
import Head from 'next/head';
import ScrollTo from 'lib/utils/ScrollTo';
import useRenderAiBot from 'views/common/hooks/useRenderAiBot';
import { PAGES_GA_IDENTIFIERS } from 'views/common/utils/constants';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import generateMetaData from 'lib/utils/generateMetaData';
import useLayoutHeights from 'views/rebranded-module/common/hooks/UseLayoutHeight';
import {
  ImgBox,
  ParticipatingLocationsSkeleton,
  ParticipatingLocationsWrapper,
  GridsGallery,
} from 'views/rebranded-module/common/components';
import useAdabtiveMultiSizeImage from 'views/common/hooks/useAdabtiveMultiSizeImage';
import { LazyLoad } from 'views/common/components';
import {
  EventTitleSection,
  EventInformationSection,
  EventInformationSectionSkeleton,
} from 'views/rebranded-module/events-module/MotorcycleEventsDetailsView/components';
import { DATE_FORMAT_READABLE } from 'views/rebranded-module/common/utils/dateFormate';
import dayjs from 'dayjs';
import { NewMainWidgetWrapper } from 'views/rebranded-module/common/components/Logical/NewMainWidgetWrapper';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';

const MotorcycleEventsDetailsView: FC<any> = (props) => {
  const { serverData, eventID } = props;
  const cmsData = useContent();
  const { LAYOUT_HEIGHT, LAYOUT_HEIGHT_SM } = useLayoutHeights();
  const handleErrorResponse = useErrorsHandler();
  const appNavigator = useAppNavigator();
  const getSizedImage = useAdabtiveMultiSizeImage();
  const dispatch = useDispatch();
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useRenderAiBot(false);
  const [data, setData] = useState(serverData);
  const [loaded, setloaded] = useState(!!serverData);
  const [showActionBar, setShowActionBar] = useState(true);
  const formatDate = (date) => {
    return dayjs(date).format(DATE_FORMAT_READABLE);
  };

  const scrollCallBack = (entries) => {
    if (entries[0].isIntersecting) {
      setShowActionBar(false);
    } else {
      setShowActionBar(true);
    }
  };
  const handleScrollToWiget = (id) => ScrollTo(id, -LAYOUT_HEIGHT_SM * 8);
  const getEventData = () => {
    dispatch(setLoading(true));
    setloaded(false);
    axios
      .get(API.events.id.replace('{id}', eventID))
      .then((res) => {
        setData(res.data);
        setloaded(true);
      })
      .catch((error) => {
        switch (error.response.status) {
          case 403:
            if (error.response.data.error_code === 'ER0001') {
              appNavigator.push(PATHS.motorcycleEvents.index());
            }
            break;
          default:
            handleErrorResponse(error, false);
        }
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };
  useEffect(() => {
    if (!serverData) {
      getEventData();
    }
    window.gtag('event', 'landing_page_view', {
      page_identifier: PAGES_GA_IDENTIFIERS.EVENT,
    });
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(scrollCallBack, {
      threshold: 0.2,
    });
    if (widgetRef.current) {
      observer.observe(widgetRef.current);
    }
    return () => {
      // disconnect the observer when the component unmounts
      observer.disconnect();
    };
  }, [widgetRef]);

  useEffect(() => {
    setData(serverData);
  }, [serverData]);
  const renameEventsGalleryArray = (galleryArray, eventImage) => {
    const modified = [];

    // Add main eventcover  first
    if (eventImage?.large_url) {
      modified.push({
        image: {
          large_url: eventImage.large_url,
        },
      });
    }

    // Then add normal gallery items
    const renamedGallery = galleryArray.map((img) => {
      if (img?.youtube_vid) {
        return {
          youtube_id: img.youtube_vid,
        };
      }
      return {
        image: {
          large_url: img.image_path,
        },
      };
    });

    return [...modified, ...renamedGallery];
  };

  return (
    <>
      {loaded && data?.event && (
        <StyledBox
          layoutHeight={LAYOUT_HEIGHT}
          smLayoutHeight={LAYOUT_HEIGHT_SM}>
          <Head>{data?.seo_values && generateMetaData(data.seo_values)}</Head>
          <Box className="EDVHeroContainer">
            <Box className="EDVBGContainer">
              <ImgBox
                image={{
                  src: getSizedImage(
                    cmsData.EventDetailsPage.heroBackground.sizes,
                  ),
                  alt: cmsData.EventDetailsPage.heroBackground.alt,
                }}
                width="100%"
                height="100%"
              />
            </Box>
            <Box className="EDContentContainer">
              <Container maxWidth="xl">
                <EventTitleSection eventTitle={data?.event?.name} />
                {(data?.gallery?.length > 0 || data?.event?.image) && (
                  <GridsGallery
                    mediaItems={renameEventsGalleryArray(
                      data.gallery,
                      data.event.image,
                    )}
                  />
                )}
                <Grid
                  container
                  columnSpacing={10}
                  justifyContent="space-between">
                  <Grid item xl={7.75} lg={7.75} md={12} xs={12}>
                    <LazyLoad
                      skeleton={<EventInformationSectionSkeleton />}
                      dataLoaded={loaded}>
                      <EventInformationSection
                        adress={data.event.address_line}
                        startDate={formatDate(data.event.start_date)}
                        endDate={formatDate(data.event.end_date)}
                        description={data.event.description}
                      />
                    </LazyLoad>
                  </Grid>
                  {/* widget side  */}
                  <Grid item xl={4.25} lg={4.25} md={12} xs={12}>
                    <Box className="EDVWidgetContainer showDESKTOP">
                      <NewMainWidgetWrapper
                        vertical
                        reservationData={{
                          pickup_location_id: data.first_bookable_location_id,
                          dropoff_location_id: data.first_bookable_location_id,
                          pickup_date: data.event.start_date,
                          dropoff_date: data.event.end_date,
                        }}
                        firstCallParams={{
                          event_id: data.event.id,
                        }}
                        showStartYourAdventureLabel={false}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          </Box>
          <Container maxWidth="xl">
            {/* Map Group Section */}
            {data.geography_component.locations_count > 0 && (
              <Box className="EDVMapSectionContainer">
                <LazyLoad
                  skeleton={<ParticipatingLocationsSkeleton />}
                  dataLoaded={loaded}>
                  <ParticipatingLocationsWrapper
                    mapGroupData={data?.geography_component}
                    placeholder={
                      cmsData.EventDetailsPage.locationSearchInputPlaceholder
                    }
                    mapGroupsTitle={interpolateString(
                      cmsData.EventDetailsPage.locationsHeading,
                      {
                        name: data.event.name,
                      },
                    )}
                    titleSizeVariant="h4"
                    content={cmsData.ParticipatingLocationsComponent}
                    target="_blank"
                  />
                </LazyLoad>
              </Box>
            )}
          </Container>
          <Box
            className="showTABLET EDVMobileWidgetContainer"
            id="event-widget"
            ref={widgetRef}>
            <Container maxWidth="xl">
              <Box className="EDVWidgetContainer">
                <NewMainWidgetWrapper
                  vertical
                  reservationData={{
                    pickup_location_id: data.first_bookable_location_id,
                    dropoff_location_id: data.first_bookable_location_id,
                    pickup_date: data.event.start_date,
                    dropoff_date: data.event.end_date,
                  }}
                  firstCallParams={{
                    event_id: data.event.id,
                  }}
                  showStartYourAdventureLabel={false}
                />
              </Box>
            </Container>
          </Box>
          {showActionBar && (
            <Box className="DVBookBtnContainer">
              <Button
                fullWidth
                variant="contained"
                className="DVBookBtn"
                onClick={() => handleScrollToWiget('event-widget')}>
                {cmsData.EventDetailsPage.reserveBtnLable}
              </Button>
            </Box>
          )}
        </StyledBox>
      )}
    </>
  );
};

export { MotorcycleEventsDetailsView };
